<script setup lang="ts">
import { computed } from "vue";
import { useApiStore } from "@/stores/api";
import RuleGroupEditor from "./RuleGroupEditor.vue";
import type { RuleGroup } from "@/utils/archival";

const store = useApiStore();

const availableServices = computed(() =>
  store.services ? Object.values(store.services) : []
);

const hasServices = computed(() => store.services !== null);

function makeGroup(): RuleGroup {
  return { type: "group", operator: "AND", negated: false, items: [] };
}

async function checkConnection() {
  await store.ensureService();
}
</script>

<template>
  <div class="vote-rule-editor">
    <div v-if="!hasServices" class="vote-rule-editor__warning">
      <p>Services not yet loaded — new service conditions cannot be added until you check the connection.</p>
      <button class="btn-check-connection" @click="checkConnection">Check connection</button>
    </div>

    <div class="vote-rule-editor__section">
      <div class="vote-rule-editor__section-header">
        <strong>Archive when:</strong>
        <button
          v-if="!store.customArchivalRules.archiveCondition"
          class="btn-add-condition"
          @click="store.customArchivalRules.archiveCondition = makeGroup()"
        >+ Add condition</button>
        <button
          v-else
          class="btn-clear-condition"
          @click="store.customArchivalRules.archiveCondition = null"
        >Clear</button>
      </div>
      <RuleGroupEditor
        v-if="store.customArchivalRules.archiveCondition"
        :group="store.customArchivalRules.archiveCondition"
        :available-services="availableServices"
        :is-root="true"
        @remove="store.customArchivalRules.archiveCondition = null"
      />
    </div>

    <div class="vote-rule-editor__section">
      <div class="vote-rule-editor__section-header">
        <strong>Unarchive when:</strong>
        <button
          v-if="!store.customArchivalRules.unarchiveCondition"
          class="btn-add-condition"
          @click="store.customArchivalRules.unarchiveCondition = makeGroup()"
        >+ Add condition</button>
        <button
          v-else
          class="btn-clear-condition"
          @click="store.customArchivalRules.unarchiveCondition = null"
        >Clear</button>
      </div>
      <RuleGroupEditor
        v-if="store.customArchivalRules.unarchiveCondition"
        :group="store.customArchivalRules.unarchiveCondition"
        :available-services="availableServices"
        :is-root="true"
        @remove="store.customArchivalRules.unarchiveCondition = null"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.vote-rule-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.vote-rule-editor__warning {
  background: color-mix(in srgb, var(--accent) 10%, var(--bg-elevated));
  border: 1px solid var(--accent);
  border-radius: var(--radius-md);
  padding: 8px 12px;
  font-size: 0.88em;

  p {
    margin: 0 0 6px;
    color: var(--text-primary);
  }
}

.btn-check-connection {
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  padding: 4px 10px;
  cursor: pointer;
  font-size: 0.85em;

  &:hover { background: var(--accent-hover); }
}

.vote-rule-editor__section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.vote-rule-editor__section-header {
  display: flex;
  align-items: center;
  gap: 8px;

  strong {
    font-size: 0.9em;
    color: var(--text-primary);
  }
}

.btn-add-condition {
  background: none;
  border: 1px dashed var(--border);
  border-radius: var(--radius-sm, 3px);
  padding: 2px 8px;
  cursor: pointer;
  color: var(--text-secondary, var(--text-primary));
  font-size: 0.82em;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
}

.btn-clear-condition {
  background: none;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm, 3px);
  padding: 2px 8px;
  cursor: pointer;
  color: var(--text-muted, #888);
  font-size: 0.82em;

  &:hover {
    border-color: var(--danger, #e55);
    color: var(--danger, #e55);
  }
}
</style>
